import React, { useState, useRef, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, Sparkles } from 'lucide-react';

interface SwipeableInsightCardProps {
  content: string;
  isLoading?: boolean;
  onFeedback: (feedbackType: 'helpful' | 'not_helpful') => void;
  language?: string;
  className?: string;
  children?: React.ReactNode;
}

const FEEDBACK_LABELS = {
  Italian: { helpful: 'Utile', notHelpful: 'Non utile', thanks: 'Grazie!' },
  English: { helpful: 'Helpful', notHelpful: 'Not helpful', thanks: 'Thanks!' },
  Spanish: { helpful: 'Util', notHelpful: 'No util', thanks: 'Gracias!' },
  German: { helpful: 'Hilfreich', notHelpful: 'Nicht hilfreich', thanks: 'Danke!' },
  French: { helpful: 'Utile', notHelpful: 'Pas utile', thanks: 'Merci!' }
};

const SwipeableInsightCard: React.FC<SwipeableInsightCardProps> = ({
  content,
  isLoading = false,
  onFeedback,
  language = 'English',
  className = '',
  children
}) => {
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<'helpful' | 'not_helpful' | null>(null);
  const [showActions, setShowActions] = useState(false);
  const startXRef = useRef(0);

  const labels = FEEDBACK_LABELS[language as keyof typeof FEEDBACK_LABELS] || FEEDBACK_LABELS.English;
  const SWIPE_THRESHOLD = 60;

  // Haptic feedback
  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (feedbackGiven || isLoading) return;
    startXRef.current = e.touches[0].clientX;
    setIsDragging(true);
  }, [feedbackGiven, isLoading]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || feedbackGiven) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - startXRef.current;

    // If actions are shown, allow right swipe to close
    if (showActions) {
      if (diff > 0) {
        const newTranslate = -130 + diff;
        setTranslateX(Math.min(newTranslate, 0));
        if (diff >= SWIPE_THRESHOLD / 2) {
          triggerHaptic();
        }
      }
      return;
    }

    // Allow left swipe only
    if (diff < 0) {
      const resistance = Math.abs(diff) > SWIPE_THRESHOLD ? 0.3 : 1;
      const newTranslate = diff * resistance;
      setTranslateX(Math.max(newTranslate, -140));

      if (Math.abs(diff) >= SWIPE_THRESHOLD && Math.abs(translateX) < SWIPE_THRESHOLD) {
        triggerHaptic();
      }
    }
  }, [isDragging, feedbackGiven, translateX, showActions, triggerHaptic]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);

    // If actions were shown and user swiped right to close
    if (showActions && translateX > -80) {
      setTranslateX(0);
      setShowActions(false);
      return;
    }

    if (Math.abs(translateX) >= SWIPE_THRESHOLD) {
      setTranslateX(-130);
      setShowActions(true);
    } else {
      setTranslateX(0);
      setShowActions(false);
    }
  }, [translateX, showActions]);

  const handleFeedback = useCallback((type: 'helpful' | 'not_helpful') => {
    setFeedbackGiven(type);
    triggerHaptic();
    onFeedback(type);
    // Animate back
    setTimeout(() => {
      setTranslateX(0);
      setShowActions(false);
    }, 150);
  }, [onFeedback, triggerHaptic]);

  const handleCancel = useCallback(() => {
    setTranslateX(0);
    setShowActions(false);
  }, []);

  if (isLoading) {
    return (
      <div className={`relative transition-all duration-500 opacity-50 ${className}`}>
        <div className="flex items-start gap-3 lg:gap-4">
          <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 shadow-lg shadow-indigo-500/20">
            <Sparkles size={18} className="text-white lg:w-6 lg:h-6" />
          </div>
          <div className="flex-1 pt-1">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
              <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
              <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce"></span>
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Background action buttons */}
      <div className="absolute inset-0 flex items-stretch justify-end rounded-2xl overflow-hidden">
        <button
          onClick={() => handleFeedback('helpful')}
          className="w-16 bg-emerald-500 flex items-center justify-center transition-opacity"
          style={{ opacity: showActions ? 1 : 0 }}
        >
          <ThumbsUp size={20} className="text-white" />
        </button>
        <button
          onClick={() => handleFeedback('not_helpful')}
          className="w-16 bg-red-500 flex items-center justify-center transition-opacity"
          style={{ opacity: Math.abs(translateX) > 20 ? 1 : 0 }}
        >
          <ThumbsDown size={20} className="text-white" />
        </button>
      </div>

      {/* Main card */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`relative z-10 bg-white lg:bg-white rounded-2xl transition-transform ${
          isDragging ? 'duration-0' : 'duration-200 ease-out'
        }`}
        style={{ transform: `translateX(${translateX}px)` }}
      >
        <div className="flex items-start gap-3 lg:gap-4">
          <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 shadow-lg shadow-indigo-500/20">
            <Sparkles size={18} className="text-white lg:w-6 lg:h-6" />
          </div>
          <div className="flex-1 pt-1">
            <p className="text-[17px] lg:text-xl font-semibold text-[#1D1D1F] leading-relaxed">
              {content}
            </p>

            {/* Additional content (memory links, etc.) */}
            {children}

            {/* Feedback indicator */}
            {feedbackGiven && (
              <div className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold ${
                feedbackGiven === 'helpful'
                  ? 'bg-emerald-50 text-emerald-600'
                  : 'bg-red-50 text-red-600'
              }`}>
                {feedbackGiven === 'helpful' ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />}
                {labels.thanks}
              </div>
            )}

            {/* Desktop feedback buttons (fallback for non-touch) */}
            {!feedbackGiven && !showActions && (
              <div className="hidden lg:flex mt-3 items-center gap-2">
                <button
                  onClick={() => handleFeedback('helpful')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                >
                  <ThumbsUp size={12} />
                  {labels.helpful}
                </button>
                <button
                  onClick={() => handleFeedback('not_helpful')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                >
                  <ThumbsDown size={12} />
                  {labels.notHelpful}
                </button>
              </div>
            )}

            {/* Swipe hint for mobile */}
            {!feedbackGiven && !showActions && translateX === 0 && (
              <div className="mt-2 text-[10px] text-black/20 font-medium lg:hidden">
                {language === 'Italian' ? '← Scorri per feedback' :
                 language === 'Spanish' ? '← Desliza para feedback' :
                 language === 'German' ? '← Wischen für Feedback' :
                 language === 'French' ? '← Glissez pour feedback' :
                 '← Swipe for feedback'}
              </div>
            )}
            {/* Close hint when actions are shown */}
            {!feedbackGiven && showActions && (
              <div className="mt-2 text-[10px] text-black/30 font-medium lg:hidden animate-pulse">
                {language === 'Italian' ? '→ Scorri per chiudere' :
                 language === 'Spanish' ? '→ Desliza para cerrar' :
                 language === 'German' ? '→ Wischen zum Schließen' :
                 language === 'French' ? '→ Glissez pour fermer' :
                 '→ Swipe to close'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop action overlay */}
      {showActions && (
        <div className="hidden lg:flex absolute inset-0 z-20 items-center justify-end gap-2 pr-3 bg-gradient-to-l from-white via-white/95 to-transparent rounded-2xl">
          <button
            onClick={handleCancel}
            className="px-3 py-1.5 text-[12px] font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            {language === 'Italian' ? 'Annulla' :
             language === 'Spanish' ? 'Cancelar' :
             language === 'German' ? 'Abbrechen' :
             language === 'French' ? 'Annuler' : 'Cancel'}
          </button>
          <button
            onClick={() => handleFeedback('helpful')}
            className="px-3 py-1.5 text-[12px] font-bold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors flex items-center gap-1"
          >
            <ThumbsUp size={12} />
            {labels.helpful}
          </button>
          <button
            onClick={() => handleFeedback('not_helpful')}
            className="px-3 py-1.5 text-[12px] font-bold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1"
          >
            <ThumbsDown size={12} />
            {labels.notHelpful}
          </button>
        </div>
      )}
    </div>
  );
};

export default SwipeableInsightCard;
